import { Loader2 } from "lucide-react"
import type { CSSProperties, ReactNode } from "react"
import { DEFERRED_IMG_PROPS } from "@/lib/deferred-img"
import { cn } from "@/lib/utils"

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("")
}

export type EntityThumbSquareProps = {
  name: string
  imageUrl?: string | null
  size?: "strip" | "overview" | "compact" | "radio"
  busy?: boolean
  busyLabel?: string
  emptyLabel?: string
  /** When no imageUrl, show this instead of name initials (e.g. skill icon). */
  fallbackIcon?: ReactNode
  className?: string
  /** Default cover. Use contain (+ imagePadded) for prop front-view style margins. */
  imageFit?: "cover" | "contain"
  /** Shrink image to 80% and center (same as prop front-view preview). Requires imageFit contain. */
  imagePadded?: boolean
  /** Background behind the image (e.g. #C0C0C0 for front-view / scale assets). */
  imageBackgroundColor?: string
}

const SIZE_CLASS: Record<NonNullable<EntityThumbSquareProps["size"]>, string> = {
  strip: "aspect-square w-full",
  overview: "h-14 w-14 shrink-0",
  compact: "h-10 w-10 shrink-0",
  radio: "h-8 w-8 shrink-0",
}

export function EntityThumbSquare({
  name,
  imageUrl,
  size = "strip",
  busy,
  busyLabel,
  emptyLabel,
  fallbackIcon,
  className,
  imageFit = "cover",
  imagePadded = false,
  imageBackgroundColor,
}: EntityThumbSquareProps) {
  const hasImage = Boolean(imageUrl)
  const paddedContain = hasImage && imageFit === "contain" && imagePadded
  const style: CSSProperties | undefined =
    hasImage && imageBackgroundColor ? { backgroundColor: imageBackgroundColor } : undefined

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-lg border border-ui-border bg-ui-surface-sunken",
        SIZE_CLASS[size],
        className,
      )}
      style={style}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          {...DEFERRED_IMG_PROPS}
          className={cn(
            paddedContain
              ? "absolute top-1/2 left-1/2 h-[80%] w-[80%] -translate-x-1/2 -translate-y-1/2 object-contain"
              : imageFit === "contain"
                ? "h-full w-full object-contain"
                : "h-full w-full object-cover",
          )}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center px-1 text-center">
          {fallbackIcon ? (
            <span className="text-ui-text-secondary">{fallbackIcon}</span>
          ) : emptyLabel ? (
            <span className="text-[9px] leading-tight text-ui-text-secondary">{emptyLabel}</span>
          ) : (
            <span className="text-xs font-semibold text-ui-text-secondary">{initials(name)}</span>
          )}
        </div>
      )}
      {busy ? (
        <span
          className="absolute top-1 right-1 flex size-4 items-center justify-center rounded-md bg-ui-bg-elevated/90"
          aria-label={busyLabel}
          role={busyLabel ? "status" : undefined}
        >
          <Loader2 className="h-3 w-3 animate-spin text-ui-accent-text" aria-hidden />
        </span>
      ) : null}
    </div>
  )
}
